import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CheckCircle,
  Flex,
  Heading,
  TagBadge,
  Text,
  Warning,
} from '@chatsdet/ui';
import { API_URL } from '../../../config.js';

type AzureDevopsConnectionCardProps = {
  connected: boolean;
  webhookActive: boolean;
  brandName: string;
  isDisconnecting: boolean;
  onDisconnect: () => void;
};

export function AzureDevopsConnectionCard({
  connected,
  webhookActive,
  brandName,
  isDisconnecting,
  onDisconnect,
}: AzureDevopsConnectionCardProps) {
  if (!connected) {
    return (
      <Card padding="lg">
        <CardHeader>
          <Flex align="start" gap="md">
            <div className="p-2 bg-warning/10 rounded-lg">
              <Warning size={24} className="text-warning" />
            </div>
            <div className="flex-1">
              <Flex align="center" gap="sm">
                <Heading level={3}>Azure DevOps Integration</Heading>
                <TagBadge name="Not Connected" color="yellow" hideDot />
              </Flex>
              <Text size="sm" className="text-muted-foreground mt-1">
                Connect Azure DevOps to create work items from issues and sync status
              </Text>
            </div>
          </Flex>
        </CardHeader>
        <CardContent>
          <Text size="sm" className="text-muted-foreground mb-4">
            Securely connect via OAuth to create Azure DevOps work items from {brandName} issues,
            auto-close issues when work items are closed, and trigger runs by tagging a work item.
          </Text>
          <Button
            variant="primary"
            onPress={() => {
              window.location.href = `${API_URL}/integrations/azure-devops/oauth/start`;
            }}
          >
            Connect with OAuth
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card padding="lg">
      <CardHeader>
        <Flex align="start" gap="md">
          <div className="p-2 bg-success/10 rounded-lg">
            <CheckCircle size={24} className="text-success" />
          </div>
          <div className="flex-1">
            <Flex align="center" gap="sm">
              <Heading level={3}>Azure DevOps Integration</Heading>
              <TagBadge name="Connected" color="blue" hideDot />
              {webhookActive && <TagBadge name="Webhooks Active" color="blue" hideDot />}
            </Flex>
            <Text size="sm" className="text-muted-foreground mt-1">
              Issues can be linked to Azure DevOps work items. Status changes sync automatically.
            </Text>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onPress={onDisconnect}
            isDisabled={isDisconnecting}
          >
            {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </Flex>
      </CardHeader>
    </Card>
  );
}
